import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import BackMethods from "../../back_component/BackMethods";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

const MAP_VIEW_STORAGE_KEY = "geoMapReport.mapView";
const PAGE_STATE_STORAGE_KEY = "geoMapReport.pageState";

const KIND_OPTIONS = [
    { value: "establishments", label: "المنشآت", color: "#0d6efd", icon: "bi-building" },
    { value: "violations", label: "الضبوط", color: "#dc3545", icon: "bi-exclamation-triangle" },
    { value: "visits", label: "الزيارات", color: "#198754", icon: "bi-geo-alt" },
];

const DefaultIcon = L.icon({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});
L.Marker.prototype.options.icon = DefaultIcon;

const UNKNOWN_DISTRICT = "غير محدد";
const SYRIA_CENTER = [34.73, 36.71];

const iconCache = new Map();
const getKindIcon = (color, appear = false) => {
    const cacheKey = `${color}|${appear ? "a" : "s"}`;
    if (!iconCache.has(cacheKey)) {
        iconCache.set(
            cacheKey,
            L.divIcon({
                className: `geo-map-pin${appear ? " geo-map-pin--appear" : ""}`,
                html: `<span style="
                    display:block;width:10px;height:10px;border-radius:50%;
                    background:${color};border:2px solid #fff;
                    box-shadow:0 1px 3px rgba(0,0,0,.35);
                "></span>`,
                iconSize: [10, 10],
                iconAnchor: [5, 5],
                popupAnchor: [0, -8],
            })
        );
    }
    return iconCache.get(cacheKey);
};

function escapeHtml(text) {
    return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/** اسم القطاع كنص بجانب النقطة — بلا خلفية */
function createDistrictNameLabel(name, beside = false) {
    return L.divIcon({
        className: "geo-district-name-label",
        html: `<div class="geo-district-name-label__text${beside ? " is-beside" : ""}">${escapeHtml(name)}</div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
    });
}

/** عنقود القطاع: العدد فقط */
function createSectorClusterIcon(color, count) {
    const size = 26;
    return L.divIcon({
        className: "geo-sector-cluster",
        html: `<div class="geo-sector-cluster__count" style="
            width:${size}px;height:${size}px;background:${color};
        ">${count}</div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });
}

function getCoords(item) {
    const lat = Number(item.lat ?? item.captureLat ?? item.establishmentLat);
    const lng = Number(item.lng ?? item.captureLng ?? item.establishmentLng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
    return [lat, lng];
}

function getDistrictName(item) {
    const name = (item?.district || item?.sector || "").trim();
    return name || UNKNOWN_DISTRICT;
}

function getDistrictCenter(points) {
    let minLat = points[0][0];
    let maxLat = points[0][0];
    let minLng = points[0][1];
    let maxLng = points[0][1];
    for (let i = 1; i < points.length; i++) {
        const [lat, lng] = points[i];
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
    }
    return [(minLat + maxLat) / 2, (minLng + maxLng) / 2];
}

function buildDistrictGroups(items) {
    const groups = new Map();
    items.forEach((item) => {
        const coords = getCoords(item);
        if (!coords) return;
        const district = getDistrictName(item);
        if (!groups.has(district)) {
            groups.set(district, { name: district, points: [] });
        }
        groups.get(district).points.push(coords);
    });

    return [...groups.keys()]
        .sort((a, b) => {
            if (a === UNKNOWN_DISTRICT) return 1;
            if (b === UNKNOWN_DISTRICT) return -1;
            return a.localeCompare(b, "ar");
        })
        .map((name) => {
            const group = groups.get(name);
            return {
                name,
                count: group.points.length,
                centroid: getDistrictCenter(group.points),
                points: group.points,
            };
        });
}

function FitBounds({ points, version, locked = false }) {
    const map = useMap();
    useEffect(() => {
        if (locked || !points?.length) return;

        const timer = setTimeout(() => {
            map.invalidateSize();
            if (points.length === 1) {
                map.setView(points[0], 14);
                return;
            }
            map.fitBounds(L.latLngBounds(points), {
                padding: [56, 56],
                maxZoom: 14,
                animate: false,
            });
        }, 60);

        return () => clearTimeout(timer);
    }, [map, points, version, locked]);
    return null;
}

/** يحفظ ويستعيد مركز/تكبير الخريطة عند التنقل خارج الصفحة */
function MapViewPersistence({ enabled }) {
    const map = useMap();

    useEffect(() => {
        if (!enabled) return undefined;

        try {
            const raw = sessionStorage.getItem(MAP_VIEW_STORAGE_KEY);
            if (raw) {
                const saved = JSON.parse(raw);
                if (saved?.center?.length === 2 && saved.zoom != null) {
                    map.setView(saved.center, saved.zoom, { animate: false });
                }
            }
        } catch {
            /* ignore */
        }

        const saveView = () => {
            try {
                const c = map.getCenter();
                sessionStorage.setItem(
                    MAP_VIEW_STORAGE_KEY,
                    JSON.stringify({
                        center: [c.lat, c.lng],
                        zoom: map.getZoom(),
                    })
                );
            } catch {
                /* ignore */
            }
        };

        map.on("moveend", saveView);
        map.on("zoomend", saveView);
        return () => {
            map.off("moveend", saveView);
            map.off("zoomend", saveView);
        };
    }, [map, enabled]);

    return null;
}

function readSavedPageState() {
    try {
        const raw = sessionStorage.getItem(PAGE_STATE_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function writeSavedPageState(state) {
    try {
        sessionStorage.setItem(PAGE_STATE_STORAGE_KEY, JSON.stringify(state));
    } catch {
        /* ignore */
    }
}

function clearSavedMapView() {
    try {
        sessionStorage.removeItem(MAP_VIEW_STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

function formatDateInput(d = new Date()) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function getTitle(item, kind) {
    if (kind === "establishments") return item.name || "منشأة";
    if (kind === "violations") return item.establishmentName || item.violationTypeName || "ضبط";
    return item.establishmentName || item.name || "زيارة";
}

function getSubtitle(item, kind) {
    if (kind === "establishments") return item.facilityType || "";
    if (kind === "violations") return item.violationTypeName || item.facilityType || "";
    return item.facilityType || item.visitType || "";
}

function getItemDayKey(item) {
    const raw = item.createdAt || item.createdAtUtc || item.date || item.fromDate;
    if (!raw) return null;
    const s = String(raw);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    return formatDateInput(d);
}

function groupItemsByDay(items) {
    const map = new Map();
    items.forEach((item) => {
        const key = getItemDayKey(item) || "غير مؤرخ";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(item);
    });
    return [...map.entries()]
        .sort((a, b) => {
            if (a[0] === "غير مؤرخ") return 1;
            if (b[0] === "غير مؤرخ") return -1;
            return a[0].localeCompare(b[0]);
        })
        .map(([key, dayItems]) => ({
            key,
            label: key === "غير مؤرخ" ? key : key,
            items: dayItems,
            count: dayItems.length,
        }));
}

function formatDayLabel(key) {
    if (!key || key === "غير مؤرخ") return "غير مؤرخ";
    const parts = key.split("-");
    if (parts.length !== 3) return key;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

export default function GeoMapReportPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { backEnd_request, getUserConstraints } = BackMethods();
    const { userGovernorate, isSuperAdmin } = getUserConstraints();

    const today = formatDateInput();
    const savedPage = useMemo(() => readSavedPageState(), []);
    const restoredFromUrl = searchParams.get("loaded") === "1";

    const [locationsData, setLocationsData] = useState([]);
    const [kind, setKind] = useState(
        () => searchParams.get("kind") || savedPage?.kind || "establishments"
    );
    const [fromDate, setFromDate] = useState(
        () => searchParams.get("from") || savedPage?.fromDate || today
    );
    const [toDate, setToDate] = useState(
        () => searchParams.get("to") || savedPage?.toDate || today
    );
    const [selectedGov, setSelectedGov] = useState(
        () => searchParams.get("gov") || savedPage?.selectedGov || userGovernorate || ""
    );
    const [selectedCity, setSelectedCity] = useState(
        () => searchParams.get("city") || savedPage?.selectedCity || ""
    );
    const [selectedSector, setSelectedSector] = useState(
        () => searchParams.get("district") || savedPage?.selectedSector || ""
    );
    const [nameQuery, setNameQuery] = useState(
        () => searchParams.get("name") || savedPage?.nameQuery || ""
    );
    const [selectedFacilityType, setSelectedFacilityType] = useState(
        () => searchParams.get("facilityType") || savedPage?.selectedFacilityType || ""
    );
    const [selectedActivityType, setSelectedActivityType] = useState(
        () => searchParams.get("activityType") || savedPage?.selectedActivityType || ""
    );
    const [facilityTypesList, setFacilityTypesList] = useState([]);
    const [activitiesList, setActivitiesList] = useState([]);

    const [items, setItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);
    const [focusedDistrict, setFocusedDistrict] = useState(
        () => searchParams.get("focus") || savedPage?.focusedDistrict || null
    );
    const [districtSearch, setDistrictSearch] = useState("");
    const [boundsVersion, setBoundsVersion] = useState(0);
    const [preserveMapView, setPreserveMapView] = useState(() => {
        try {
            return restoredFromUrl && !!sessionStorage.getItem(MAP_VIEW_STORAGE_KEY);
        } catch {
            return false;
        }
    });
    const [filtersOpen, setFiltersOpen] = useState(
        () => searchParams.get("filters") !== "0" && savedPage?.filtersOpen !== false
    );
    const [showProgressiveSetup, setShowProgressiveSetup] = useState(false);
    const [progressiveSeconds, setProgressiveSeconds] = useState(20);
    const [playbackMode, setPlaybackMode] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackItems, setPlaybackItems] = useState([]);
    const [playbackDays, setPlaybackDays] = useState([]);
    const [playbackDayIndex, setPlaybackDayIndex] = useState(-1);
    const playbackTimerRef = useRef(null);
    const didAutoRestoreRef = useRef(false);

    const isLocationMode = Boolean(
        selectedFacilityType.trim() || selectedActivityType.trim()
    );

    const stopProgressiveTimers = () => {
        if (playbackTimerRef.current) {
            clearTimeout(playbackTimerRef.current);
            playbackTimerRef.current = null;
        }
    };

    const buildUrlParams = (overrides = {}) => {
        const facility = overrides.facilityType ?? selectedFacilityType;
        const activity = overrides.activityType ?? selectedActivityType;
        const locationMode = Boolean(String(facility || "").trim() || String(activity || "").trim());
        const params = {
            kind: overrides.kind ?? kind,
            from: locationMode ? "" : (overrides.from ?? fromDate),
            to: locationMode ? "" : (overrides.to ?? toDate),
            gov: overrides.gov ?? selectedGov,
            city: overrides.city ?? selectedCity,
            district: overrides.district ?? selectedSector,
            name: overrides.name ?? nameQuery,
            facilityType: facility,
            activityType: activity,
            focus: overrides.focus !== undefined ? overrides.focus : focusedDistrict || "",
            filters: (overrides.filtersOpen ?? filtersOpen) ? "1" : "0",
            loaded:
                overrides.loaded ??
                (hasFetched || searchParams.get("loaded") === "1" ? "1" : ""),
            mode: locationMode ? "location" : "date",
        };
        Object.keys(params).forEach((key) => {
            if (params[key] === "" || params[key] == null) delete params[key];
        });
        return params;
    };

    const syncUrlAndStorage = (overrides = {}) => {
        const params = buildUrlParams(overrides);
        setSearchParams(params, { replace: true });
        writeSavedPageState({
            kind: params.kind || "establishments",
            fromDate: params.from || fromDate,
            toDate: params.to || toDate,
            selectedGov: params.gov || "",
            selectedCity: params.city || "",
            selectedSector: params.district || "",
            nameQuery: params.name || "",
            selectedFacilityType: params.facilityType || "",
            selectedActivityType: params.activityType || "",
            focusedDistrict: params.focus || null,
            filtersOpen: params.filters !== "0",
            wasLoaded: params.loaded === "1",
        });
    };

    useEffect(() => () => stopProgressiveTimers(), []);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [locRes, facRes, actRes] = await Promise.all([
                    backEnd_request.get("/Locations?includeInactive=false", {
                        params: { size: 10000 },
                    }),
                    backEnd_request.get("/FacilityTypes"),
                    backEnd_request.get("/Activities?page=1&size=200"),
                ]);
                setLocationsData(locRes.data?.data || []);
                setFacilityTypesList(facRes.data || []);
                setActivitiesList(actRes.data?.data || []);
            } catch (err) {
                console.error("Error fetching initial data:", err);
            }
        };
        fetchInitialData();
    }, []);

    const availableGovernorates = useMemo(
        () => [...new Set(locationsData.map((l) => l.governorate).filter(Boolean))],
        [locationsData]
    );
    const availableCities = useMemo(() => {
        if (!selectedGov) return [];
        return [
            ...new Set(
                locationsData
                    .filter((l) => l.governorate === selectedGov)
                    .map((l) => l.city)
                    .filter(Boolean)
            ),
        ];
    }, [locationsData, selectedGov]);
    const availableSectors = useMemo(() => {
        if (!selectedCity) return [];
        return locationsData
            .filter((l) => l.governorate === selectedGov && l.city === selectedCity)
            .map((l) => l.sector)
            .filter(Boolean);
    }, [locationsData, selectedGov, selectedCity]);

    const displayItems = playbackMode ? playbackItems : items;
    const dayGroupsPreview = useMemo(() => groupItemsByDay(items), [items]);

    const districtGroups = useMemo(() => buildDistrictGroups(displayItems), [displayItems]);

    const filteredDistrictGroups = useMemo(() => {
        const q = districtSearch.trim();
        if (!q) return districtGroups;
        return districtGroups.filter((g) => g.name.includes(q));
    }, [districtGroups, districtSearch]);

    const visibleItems = useMemo(() => {
        if (!focusedDistrict) return displayItems;
        return displayItems.filter((item) => getDistrictName(item) === focusedDistrict);
    }, [displayItems, focusedDistrict]);

    const visiblePoints = useMemo(
        () => visibleItems.map((item) => getCoords(item)).filter(Boolean),
        [visibleItems]
    );

    const allItemsPoints = useMemo(
        () => items.map((item) => getCoords(item)).filter(Boolean),
        [items]
    );

    const mapFitPoints = playbackMode ? allItemsPoints : visiblePoints;

    const currentDayKey =
        playbackMode && playbackDayIndex >= 0
            ? playbackDays[playbackDayIndex]?.key
            : null;

    const visibleDistrictLabels = useMemo(() => {
        const sourceItems = focusedDistrict
            ? displayItems.filter((item) => getDistrictName(item) === focusedDistrict)
            : displayItems;
        return buildDistrictGroups(sourceItems);
    }, [displayItems, focusedDistrict]);

    const kindMeta = KIND_OPTIONS.find((k) => k.value === kind) || KIND_OPTIONS[0];
    const pinIcon = getKindIcon(kindMeta.color, false);
    const pinIconAppear = getKindIcon(kindMeta.color, true);
    const showIndividualPins = playbackMode || !!focusedDistrict;

    const stepSecondsPreview =
        dayGroupsPreview.length > 0
            ? (progressiveSeconds / dayGroupsPreview.length).toFixed(1)
            : "—";

    const exitProgressive = () => {
        stopProgressiveTimers();
        setIsPlaying(false);
        setPlaybackMode(false);
        setPlaybackItems([]);
        setPlaybackDays([]);
        setPlaybackDayIndex(-1);
        setPreserveMapView(false);
        setBoundsVersion((v) => v + 1);
    };

    const runProgressiveWithData = (dataItems) => {
        stopProgressiveTimers();
        const days = groupItemsByDay(dataItems);
        if (!days.length) {
            setError("لا توجد بيانات مؤرخة لتشغيل العرض التقدمي");
            return;
        }

        setFiltersOpen(false);
        setShowProgressiveSetup(false);
        setFocusedDistrict(null);
        setDistrictSearch("");
        setPlaybackDays(days);
        setPlaybackMode(true);
        setIsPlaying(true);
        setPreserveMapView(false);

        const stepMs = Math.max(100, (progressiveSeconds * 1000) / days.length);
        let index = 1;
        let accumulated = days[0].items.slice();
        setPlaybackItems(accumulated);
        setPlaybackDayIndex(0);
        setBoundsVersion((v) => v + 1);

        // ثبّت عرض الخريطة بعد الضبط الأولي حتى لا يُعاد التصغير عند كل يوم
        setTimeout(() => setPreserveMapView(true), 180);

        if (days.length === 1) {
            setIsPlaying(false);
            return;
        }

        const tick = () => {
            accumulated = accumulated.concat(days[index].items);
            setPlaybackItems(accumulated.slice());
            setPlaybackDayIndex(index);
            index += 1;
            if (index < days.length) {
                playbackTimerRef.current = setTimeout(tick, stepMs);
            } else {
                setIsPlaying(false);
                playbackTimerRef.current = null;
            }
        };

        playbackTimerRef.current = setTimeout(tick, stepMs);
    };

    const fetchGeoData = async ({ forProgressive = false, fromRestore = false } = {}) => {
        if (isLocationMode) {
            if (!selectedSector) {
                setError("يجب اختيار القطاع عند البحث حسب نوع الفعالية أو النشاط");
                return [];
            }
        } else if (!fromDate || !toDate) {
            setError("يجب تحديد فترة التاريخ، أو اختيار نوع فعالية/نشاط للبحث بدون تاريخ");
            return [];
        }

        setIsLoading(true);
        setError(null);
        if (!forProgressive && !fromRestore) {
            exitProgressive();
            setFocusedDistrict(null);
            setDistrictSearch("");
            clearSavedMapView();
            setPreserveMapView(false);
        }
        try {
            const params = { kind };
            if (selectedGov) params.governorate = selectedGov;
            if (selectedCity) params.city = selectedCity;
            if (selectedSector) params.district = selectedSector;
            if (nameQuery.trim()) params.name = nameQuery.trim();

            let endpoint = "/admin/reports/geo";
            if (isLocationMode) {
                endpoint = "/admin/reports/geo/by-location";
                if (selectedFacilityType.trim()) params.facilityType = selectedFacilityType.trim();
                if (selectedActivityType.trim()) params.activityType = selectedActivityType.trim();
            } else {
                if (fromDate) params.from = fromDate;
                if (toDate) params.to = toDate;
                if (fromDate && toDate && fromDate === toDate) params.date = fromDate;
            }

            const res = await backEnd_request.get(endpoint, { params });
            const payload = res.data || {};
            const data = Array.isArray(payload.data) ? payload.data : [];
            setItems(data);
            setTotalCount(payload.count ?? data.length ?? 0);
            setHasFetched(true);

            if (fromRestore) {
                try {
                    if (sessionStorage.getItem(MAP_VIEW_STORAGE_KEY)) {
                        setPreserveMapView(true);
                    } else {
                        setBoundsVersion((v) => v + 1);
                    }
                } catch {
                    setBoundsVersion((v) => v + 1);
                }
            } else {
                setBoundsVersion((v) => v + 1);
            }

            const focusValue = fromRestore
                ? focusedDistrict || searchParams.get("focus") || ""
                : "";
            if (fromRestore && focusValue) {
                setFocusedDistrict(focusValue);
            }

            syncUrlAndStorage({
                loaded: "1",
                focus: focusValue || "",
            });
            return data;
        } catch (err) {
            console.error("Geo report error:", err);
            setItems([]);
            setTotalCount(0);
            setError(err.response?.data?.message || err.response?.data || "تعذر جلب بيانات الخريطة");
            setHasFetched(true);
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (didAutoRestoreRef.current) return;
        const urlLoaded = searchParams.get("loaded") === "1";
        const sessionLoaded = !!savedPage?.wasLoaded;
        if (!urlLoaded && !sessionLoaded) return;
        didAutoRestoreRef.current = true;
        fetchGeoData({ fromRestore: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startProgressive = async () => {
        if (isLocationMode) {
            setError("العرض التقدمي متاح فقط عند البحث بالتاريخ");
            return;
        }
        let data = items;
        if (!hasFetched || !items.length) {
            data = await fetchGeoData({ forProgressive: true });
        }
        if (!data?.length) {
            setError("لا توجد نقاط لتشغيل العرض التقدمي");
            return;
        }
        runProgressiveWithData(data);
    };

    const resetFilters = () => {
        exitProgressive();
        clearSavedMapView();
        try {
            sessionStorage.removeItem(PAGE_STATE_STORAGE_KEY);
        } catch {
            /* ignore */
        }
        const defaultGov = isSuperAdmin ? "" : userGovernorate;
        setKind("establishments");
        setFromDate(today);
        setToDate(today);
        setSelectedGov(defaultGov || "");
        setSelectedCity("");
        setSelectedSector("");
        setNameQuery("");
        setSelectedFacilityType("");
        setSelectedActivityType("");
        setItems([]);
        setTotalCount(0);
        setError(null);
        setHasFetched(false);
        setFocusedDistrict(null);
        setDistrictSearch("");
        setFiltersOpen(true);
        setShowProgressiveSetup(false);
        setPreserveMapView(false);
        setSearchParams({}, { replace: true });
    };

    const focusDistrict = (name) => {
        if (focusedDistrict === name) {
            setFocusedDistrict(null);
            setPreserveMapView(false);
            setBoundsVersion((v) => v + 1);
            syncUrlAndStorage({ focus: "", loaded: "1" });
            return;
        }
        setFocusedDistrict(name);
        setPreserveMapView(false);
        setBoundsVersion((v) => v + 1);
        syncUrlAndStorage({ focus: name, loaded: "1" });
    };

    const clearDistrictFocus = () => {
        setFocusedDistrict(null);
        setPreserveMapView(false);
        setBoundsVersion((v) => v + 1);
        syncUrlAndStorage({ focus: "", loaded: "1" });
    };

    const openDetails = (item) => {
        syncUrlAndStorage({ loaded: "1" });
        if (kind === "establishments" && (item.fieldUID || item.establishmentId)) {
            navigate(`/admin/establishments/${item.fieldUID || item.establishmentId}`);
            return;
        }
        if (kind === "visits") {
            const uid = item.visitUid || item.inspectionVisitUid || item.uid;
            if (uid) {
                navigate(`/admin/visit-details/${uid}`);
                return;
            }
        }
        if (kind === "violations") {
            if (item.reportNumber) {
                navigate(`/admin/violation-details?id=${item.reportNumber}`);
                return;
            }
            if (item.establishmentId) {
                navigate(`/admin/establishments/${item.establishmentId}`);
            }
        }
    };

    const showMap = hasFetched && !isLoading && (playbackMode ? mapFitPoints.length > 0 : visiblePoints.length > 0);

    return (
        <div className="container-fluid py-4" style={{ backgroundColor: "#f4f7f6", direction: "rtl", minHeight: "100vh" }}>
            <style>{`
                .geo-map-pin.leaflet-div-icon,
                .geo-district-name-label.leaflet-div-icon,
                .geo-sector-cluster.leaflet-div-icon {
                    background: transparent !important;
                    border: none !important;
                }
                .geo-district-name-label__text {
                    transform: translate(-50%, -50%);
                    pointer-events: none;
                    direction: rtl;
                    font-weight: 700;
                    font-size: 12px;
                    line-height: 1.2;
                    color: #0f172a;
                    white-space: nowrap;
                    background: transparent;
                    padding: 0;
                    text-shadow:
                        0 0 3px #fff,
                        1px 0 0 #fff,
                        -1px 0 0 #fff,
                        0 1px 0 #fff,
                        0 -1px 0 #fff;
                }
                .geo-district-name-label__text.is-beside {
                    transform: translate(18px, -50%);
                }
                .geo-sector-cluster__count {
                    border-radius: 50%;
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 10px;
                    border: 2px solid #fff;
                    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.25);
                    cursor: pointer;
                }
                @keyframes geo-pin-pop {
                    0% { transform: scale(0); opacity: 0; }
                    65% { transform: scale(1.4); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                }
                .geo-map-pin--appear span {
                    animation: geo-pin-pop 0.5s ease-out;
                }
                .geo-district-item {
                    cursor: pointer;
                    transition: background .15s ease, border-color .15s ease;
                }
                .geo-district-item:hover {
                    background: #f8fafc;
                }
                .geo-district-item.is-active {
                    background: #eef5ff;
                    border-color: #0d6efd !important;
                }
            `}</style>

            <div className="card border-0 shadow-sm rounded-4 mb-3 overflow-hidden">
                <div className="card-body p-3 p-md-4 bg-white">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-0">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <h2 className="fw-bold text-dark mb-0 fs-4">الخريطة الجغرافية</h2>
                            <span
                                className="badge rounded-pill px-3 py-2"
                                style={{ backgroundColor: `${kindMeta.color}18`, color: kindMeta.color }}
                            >
                                {hasFetched ? `النتائج: ${totalCount}` : "اختر الفلاتر ثم اعرض"}
                            </span>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            <button
                                type="button"
                                className={`btn btn-sm rounded-pill ${filtersOpen ? "btn-primary" : "btn-outline-primary"}`}
                                onClick={() => {
                                    setFiltersOpen((v) => {
                                        const next = !v;
                                        syncUrlAndStorage({ filtersOpen: next, loaded: hasFetched ? "1" : "" });
                                        return next;
                                    });
                                }}
                            >
                                <i className={`bi ${filtersOpen ? "bi-funnel-fill" : "bi-funnel"} me-1`}></i>
                                {filtersOpen ? "إخفاء الفلاتر" : "إظهار الفلاتر"}
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm rounded-pill ${
                                    showProgressiveSetup || playbackMode ? "btn-dark" : "btn-outline-dark"
                                }`}
                                onClick={() => {
                                    if (playbackMode || isLocationMode) return;
                                    setShowProgressiveSetup((v) => !v);
                                    if (!filtersOpen) setFiltersOpen(true);
                                }}
                                disabled={isPlaying || isLocationMode}
                                title={isLocationMode ? "العرض التقدمي متاح مع البحث بالتاريخ فقط" : undefined}
                            >
                                <i className="bi bi-play-circle me-1"></i>
                                عرض تقدمي
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-link text-muted text-decoration-none"
                                onClick={resetFilters}
                            >
                                <i className="bi bi-arrow-counterclockwise"></i> إعادة ضبط
                            </button>
                        </div>
                    </div>

                    {showProgressiveSetup && !playbackMode && (
                        <div className="mt-3 p-3 rounded-4 border bg-light">
                            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                                <div>
                                    <div className="fw-bold mb-1">إعداد العرض التقدمي</div>
                                    <div className="small text-muted">
                                        يُقسَّم الزمن على عدد الأيام، وتظهر دبابيس كل يوم تباعاً كأنيميشن.
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary rounded-pill"
                                    onClick={() => setShowProgressiveSetup(false)}
                                >
                                    إغلاق
                                </button>
                            </div>
                            <div className="row g-3 align-items-end mt-1">
                                <div className="col-md-6">
                                    <label className="small fw-bold text-muted mb-1">
                                        مدة العرض: {progressiveSeconds} ثانية
                                    </label>
                                    <input
                                        type="range"
                                        className="form-range"
                                        min={10}
                                        max={60}
                                        step={1}
                                        value={progressiveSeconds}
                                        onChange={(e) => setProgressiveSeconds(Number(e.target.value))}
                                    />
                                    <div className="d-flex justify-content-between small text-muted">
                                        <span>10 ث</span>
                                        <span>60 ث</span>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="small text-muted mb-1">عدد الأيام في البيانات</div>
                                    <div className="fw-bold">
                                        {hasFetched ? dayGroupsPreview.length : "— (بعد الجلب)"}
                                    </div>
                                    <div className="small text-muted">
                                        لكل يوم ≈ {stepSecondsPreview} ث
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <button
                                        type="button"
                                        className="btn btn-dark w-100 rounded-pill fw-bold"
                                        onClick={startProgressive}
                                        disabled={isLoading || isPlaying}
                                    >
                                        {isLoading ? (
                                            <span className="spinner-border spinner-border-sm me-1" />
                                        ) : (
                                            <i className="bi bi-play-fill me-1"></i>
                                        )}
                                        بدء العرض
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {filtersOpen && (
                    <div className="mt-3">
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            {KIND_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`btn btn-sm rounded-pill px-3 ${
                                        kind === opt.value ? "text-white" : "btn-light border"
                                    }`}
                                    style={
                                        kind === opt.value
                                            ? { backgroundColor: opt.color, borderColor: opt.color }
                                            : undefined
                                    }
                                    onClick={() => {
                                        exitProgressive();
                                        setKind(opt.value);
                                        setItems([]);
                                        setHasFetched(false);
                                        setFocusedDistrict(null);
                                    }}
                                    disabled={isPlaying}
                                >
                                    <i className={`bi ${opt.icon} me-1`}></i>
                                    {opt.label}
                                </button>
                            ))}
                        </div>

                        {isLocationMode && (
                            <div className="alert alert-info py-2 px-3 small mb-2 rounded-3">
                                تم تفعيل البحث حسب الموقع (بدون تاريخ). القطاع مطلوب.
                                لإعادة البحث بالتاريخ امسح نوع الفعالية والنشاط.
                            </div>
                        )}

                        <div className="row g-2 align-items-end flex-nowrap" style={{ overflowX: "auto" }}>
                            <div className="col" style={{ minWidth: 140 }}>
                                <label className="small text-muted mb-1 fw-bold">من تاريخ</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm border-0 bg-light shadow-sm"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    disabled={isPlaying || isLocationMode}
                                />
                            </div>
                            <div className="col" style={{ minWidth: 140 }}>
                                <label className="small text-muted mb-1 fw-bold">إلى تاريخ</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm border-0 bg-light shadow-sm"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    disabled={isPlaying || isLocationMode}
                                />
                            </div>
                            <div className="col" style={{ minWidth: 120 }}>
                                <label className="small text-muted mb-1 fw-bold">المحافظة</label>
                                <select
                                    className="form-select form-select-sm border-0 bg-light shadow-sm"
                                    value={selectedGov}
                                    disabled={!isSuperAdmin || isPlaying}
                                    onChange={(e) => {
                                        setSelectedGov(e.target.value);
                                        setSelectedCity("");
                                        setSelectedSector("");
                                    }}
                                >
                                    <option value="">كل المحافظات</option>
                                    {availableGovernorates.map((g) => (
                                        <option key={g} value={g}>{g}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col" style={{ minWidth: 120 }}>
                                <label className="small text-muted mb-1 fw-bold">المدينة</label>
                                <select
                                    className="form-select form-select-sm border-0 bg-light shadow-sm"
                                    value={selectedCity}
                                    disabled={!selectedGov || isPlaying}
                                    onChange={(e) => {
                                        setSelectedCity(e.target.value);
                                        setSelectedSector("");
                                    }}
                                >
                                    <option value="">كل المدن</option>
                                    {availableCities.map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col" style={{ minWidth: 130 }}>
                                <label className={`small mb-1 fw-bold ${isLocationMode ? "text-danger" : "text-muted"}`}>
                                    القطاع{isLocationMode ? " *" : ""}
                                </label>
                                <select
                                    className={`form-select form-select-sm border-0 shadow-sm ${
                                        isLocationMode && !selectedSector ? "bg-warning-subtle" : "bg-light"
                                    }`}
                                    value={selectedSector}
                                    disabled={!selectedCity || isPlaying}
                                    onChange={(e) => setSelectedSector(e.target.value)}
                                >
                                    <option value="">{isLocationMode ? "اختر القطاع" : "كل القطاعات"}</option>
                                    {availableSectors.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col" style={{ minWidth: 130 }}>
                                <label className="small text-muted mb-1 fw-bold">نوع الفعالية</label>
                                <input
                                    list="geoFacilityTypesOptions"
                                    className="form-control form-control-sm border-0 bg-light shadow-sm"
                                    value={selectedFacilityType}
                                    placeholder="بحث..."
                                    onChange={(e) => {
                                        exitProgressive();
                                        setShowProgressiveSetup(false);
                                        setSelectedFacilityType(e.target.value);
                                    }}
                                    disabled={isPlaying}
                                />
                                <datalist id="geoFacilityTypesOptions">
                                    {facilityTypesList.map((f) => (
                                        <option key={f.facilityTypeId} value={f.name} />
                                    ))}
                                </datalist>
                            </div>
                            <div className="col" style={{ minWidth: 130 }}>
                                <label className="small text-muted mb-1 fw-bold">النشاط</label>
                                <input
                                    list="geoActivitiesOptions"
                                    className="form-control form-control-sm border-0 bg-light shadow-sm"
                                    value={selectedActivityType}
                                    placeholder="بحث..."
                                    onChange={(e) => {
                                        exitProgressive();
                                        setShowProgressiveSetup(false);
                                        setSelectedActivityType(e.target.value);
                                    }}
                                    disabled={isPlaying}
                                />
                                <datalist id="geoActivitiesOptions">
                                    {activitiesList.map((a) => (
                                        <option key={a.activityId} value={a.activityType} />
                                    ))}
                                </datalist>
                            </div>
                            <div className="col" style={{ minWidth: 140 }}>
                                <label className="small text-muted mb-1 fw-bold">اسم المنشأة</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm border-0 bg-light shadow-sm"
                                    placeholder="بحث..."
                                    value={nameQuery}
                                    onChange={(e) => setNameQuery(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && fetchGeoData()}
                                    disabled={isPlaying}
                                />
                            </div>
                            <div className="col-auto">
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm shadow-sm fw-bold border-0 px-3"
                                    style={{ height: "31px", borderRadius: "8px", whiteSpace: "nowrap" }}
                                    onClick={() => fetchGeoData()}
                                    disabled={isLoading || isPlaying}
                                >
                                    {isLoading ? (
                                        <span className="spinner-border spinner-border-sm me-1" />
                                    ) : (
                                        <i className="bi bi-map-fill me-1"></i>
                                    )}
                                    عرض
                                </button>
                            </div>
                        </div>
                    </div>
                    )}
                </div>
            </div>

            {playbackMode && (
                <div className="card border-0 shadow-sm rounded-4 mb-3 overflow-hidden">
                    <div className="card-body py-3 px-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            <span
                                className={`badge rounded-pill px-3 py-2 ${isPlaying ? "bg-dark" : "bg-success"}`}
                            >
                                {isPlaying ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" />
                                        جاري العرض التقدمي
                                    </>
                                ) : (
                                    "انتهى العرض التقدمي"
                                )}
                            </span>
                            {playbackDayIndex >= 0 && playbackDays[playbackDayIndex] && (
                                <div className="fw-bold">
                                    اليوم: {formatDayLabel(playbackDays[playbackDayIndex].key)}
                                    <span className="text-muted fw-normal small ms-2">
                                        ({playbackDayIndex + 1} / {playbackDays.length})
                                    </span>
                                </div>
                            )}
                            <div className="small text-muted">
                                النقاط الظاهرة: {playbackItems.length}
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            {isPlaying && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger rounded-pill"
                                    onClick={() => {
                                        stopProgressiveTimers();
                                        setIsPlaying(false);
                                    }}
                                >
                                    إيقاف
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary rounded-pill"
                                onClick={exitProgressive}
                            >
                                إنهاء والعودة للعرض العادي
                            </button>
                        </div>
                        {playbackDays.length > 0 && (
                            <div className="w-100">
                                <div className="progress" style={{ height: 6 }}>
                                    <div
                                        className="progress-bar bg-dark"
                                        style={{
                                            width: `${
                                                playbackDays.length
                                                    ? ((playbackDayIndex + 1) / playbackDays.length) * 100
                                                    : 0
                                            }%`,
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {error && (
                <div className="alert alert-danger rounded-4 shadow-sm">
                    {typeof error === "string" ? error : JSON.stringify(error)}
                </div>
            )}

            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 px-3 py-2 bg-white border-bottom">
                    <div className="d-flex align-items-center gap-2 small text-muted">
                        <span
                            style={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                background: kindMeta.color,
                                display: "inline-block",
                            }}
                        />
                        دبابيس {kindMeta.label}
                        {districtGroups.length > 0 && (
                            <span className="badge bg-light text-dark border">
                                {districtGroups.length} قطاع
                            </span>
                        )}
                        {focusedDistrict && (
                            <span
                                className="badge rounded-pill"
                                style={{ backgroundColor: `${kindMeta.color}18`, color: kindMeta.color }}
                            >
                                التركيز: {focusedDistrict}
                            </span>
                        )}
                        {visiblePoints.length > 0 && visiblePoints.length !== totalCount && !focusedDistrict && (
                            <span className="text-warning">
                                (مع إحداثيات: {visiblePoints.length} من {totalCount})
                            </span>
                        )}
                    </div>
                    {focusedDistrict && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary rounded-pill"
                            onClick={clearDistrictFocus}
                        >
                            عرض كل القطاعات
                        </button>
                    )}
                </div>

                <div className="row g-0">
                    {hasFetched && districtGroups.length > 0 && (
                        <div className="col-12 col-lg-3 border-start bg-white">
                            <div className="p-3 border-bottom">
                                <div className="fw-bold small mb-2">القطاعات</div>
                                <input
                                    type="text"
                                    className="form-control form-control-sm bg-light border-0"
                                    placeholder="بحث عن قطاع..."
                                    value={districtSearch}
                                    onChange={(e) => setDistrictSearch(e.target.value)}
                                />
                            </div>
                            <div style={{ maxHeight: "min(62vh, 640px)", overflowY: "auto" }}>
                                <button
                                    type="button"
                                    className={`geo-district-item w-100 text-end border-0 border-bottom px-3 py-2 bg-transparent ${
                                        !focusedDistrict ? "is-active" : ""
                                    }`}
                                    onClick={clearDistrictFocus}
                                >
                                    <div className="d-flex justify-content-between align-items-center gap-2">
                                        <span className="fw-semibold small">كل القطاعات</span>
                                        <span className="badge bg-secondary-subtle text-secondary">
                                            {districtGroups.reduce((s, g) => s + g.count, 0)}
                                        </span>
                                    </div>
                                </button>
                                {filteredDistrictGroups.map((g) => (
                                    <button
                                        key={g.name}
                                        type="button"
                                        className={`geo-district-item w-100 text-end border-0 border-bottom px-3 py-2 bg-transparent ${
                                            focusedDistrict === g.name ? "is-active" : ""
                                        }`}
                                        onClick={() => focusDistrict(g.name)}
                                    >
                                        <div className="d-flex justify-content-between align-items-center gap-2">
                                            <span className="small text-dark text-truncate" title={g.name}>
                                                {g.name}
                                            </span>
                                            <span
                                                className="badge"
                                                style={{
                                                    backgroundColor: `${kindMeta.color}18`,
                                                    color: kindMeta.color,
                                                }}
                                            >
                                                {g.count}
                                            </span>
                                        </div>
                                    </button>
                                ))}
                                {filteredDistrictGroups.length === 0 && (
                                    <div className="text-center text-muted small py-4">لا نتائج</div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className={hasFetched && districtGroups.length > 0 ? "col-12 col-lg-9" : "col-12"}>
                        <div style={{ height: "min(70vh, 720px)", position: "relative" }}>
                            {playbackMode && playbackDayIndex >= 0 && playbackDays[playbackDayIndex] && (
                                <div
                                    className="position-absolute top-0 start-50 translate-middle-x mt-3 px-3 py-2 rounded-pill shadow-sm fw-bold"
                                    style={{
                                        zIndex: 1100,
                                        background: "rgba(255,255,255,.92)",
                                        border: `2px solid ${kindMeta.color}`,
                                        color: "#111",
                                    }}
                                >
                                    {formatDayLabel(playbackDays[playbackDayIndex].key)}
                                    <span className="text-muted fw-normal small ms-2">
                                        +{playbackDays[playbackDayIndex].count} نقطة
                                    </span>
                                </div>
                            )}
                            {isLoading && (
                                <div
                                    className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
                                    style={{ zIndex: 1000, background: "rgba(255,255,255,.65)" }}
                                >
                                    <div className="spinner-grow text-primary" />
                                    <p className="mt-3 text-muted mb-0">جاري تحميل النقاط...</p>
                                </div>
                            )}

                            {!hasFetched && !isLoading ? (
                                <div className="h-100 d-flex flex-column align-items-center justify-content-center bg-white text-muted">
                                    <i className="bi bi-map display-3 opacity-25"></i>
                                    <p className="mt-3 mb-0">حدّد الفلاتر ثم اضغط «عرض على الخريطة»</p>
                                </div>
                            ) : hasFetched && !isLoading && !showMap ? (
                                <div className="h-100 d-flex flex-column align-items-center justify-content-center bg-white text-muted">
                                    <i className="bi bi-geo-alt display-3 opacity-25"></i>
                                    <h5 className="mt-3 text-dark">لا توجد نقاط مطابقة</h5>
                                    <p className="mb-0">جرّب تغيير التاريخ أو الموقع أو نوع العرض.</p>
                                </div>
                            ) : showMap ? (
                                <MapContainer
                                    key={`map-${kind}`}
                                    center={mapFitPoints[0] || SYRIA_CENTER}
                                    zoom={12}
                                    style={{ height: "100%", width: "100%" }}
                                    scrollWheelZoom
                                >
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    <FitBounds
                                        points={mapFitPoints}
                                        version={boundsVersion}
                                        locked={preserveMapView}
                                    />
                                    <MapViewPersistence enabled={hasFetched && !isPlaying} />

                                    {/* اسم القطاع بجانب العدد (أو في الوسط عند عرض النقاط) */}
                                    {visibleDistrictLabels.map((g) => (
                                        <Marker
                                            key={`label-${g.name}`}
                                            position={g.centroid}
                                            icon={createDistrictNameLabel(g.name, !showIndividualPins)}
                                            interactive={false}
                                            keyboard={false}
                                            zIndexOffset={1000}
                                        />
                                    ))}

                                    {/* الوضع العادي: عنقود العدد فقط لكل قطاع */}
                                    {!showIndividualPins &&
                                        visibleDistrictLabels.map((g) => (
                                            <Marker
                                                key={`sector-${g.name}`}
                                                position={g.centroid}
                                                icon={createSectorClusterIcon(kindMeta.color, g.count)}
                                                eventHandlers={{
                                                    click: () => focusDistrict(g.name),
                                                }}
                                                zIndexOffset={400}
                                            >
                                                <Popup>
                                                    <div dir="rtl" style={{ minWidth: 140, textAlign: "right" }}>
                                                        <div className="fw-bold mb-1">{g.name}</div>
                                                        <div className="small text-muted mb-2">
                                                            {g.count} نقطة
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary w-100"
                                                            onClick={() => focusDistrict(g.name)}
                                                        >
                                                            عرض النقاط
                                                        </button>
                                                    </div>
                                                </Popup>
                                            </Marker>
                                        ))}

                                    {/* نقاط فردية: عرض تقدمي أو قطاع مركّز */}
                                    {showIndividualPins &&
                                        visibleItems.map((item, idx) => {
                                            const coords = getCoords(item);
                                            if (!coords) return null;
                                            const districtName = getDistrictName(item);
                                            const key =
                                                item.establishmentId ||
                                                item.violationReportId ||
                                                item.visitUid ||
                                                item.inspectionVisitId ||
                                                `${coords[0]}-${coords[1]}-${idx}`;
                                            const title = getTitle(item, kind);
                                            const subtitle = getSubtitle(item, kind);
                                            const locationLabel = [
                                                item.city,
                                                districtName !== UNKNOWN_DISTRICT ? districtName : null,
                                            ]
                                                .filter(Boolean)
                                                .join(" — ");
                                            const canOpen =
                                                (kind === "establishments" &&
                                                    (item.fieldUID || item.establishmentId)) ||
                                                (kind === "visits" &&
                                                    (item.visitUid ||
                                                        item.inspectionVisitUid ||
                                                        item.uid)) ||
                                                (kind === "violations" &&
                                                    (item.reportNumber || item.establishmentId));
                                            const isNewToday =
                                                playbackMode &&
                                                currentDayKey &&
                                                getItemDayKey(item) === currentDayKey;

                                            return (
                                                <Marker
                                                    key={key}
                                                    position={coords}
                                                    icon={isNewToday ? pinIconAppear : pinIcon}
                                                >
                                                    <Popup>
                                                        <div dir="rtl" style={{ minWidth: 180, textAlign: "right" }}>
                                                            <div className="fw-bold mb-1">{title}</div>
                                                            {subtitle && (
                                                                <div className="small text-muted mb-1">{subtitle}</div>
                                                            )}
                                                            {(locationLabel || districtName) && (
                                                                <div className="small mb-2 text-muted">
                                                                    <i
                                                                        className="bi bi-geo-alt-fill me-1"
                                                                        style={{ color: kindMeta.color }}
                                                                    ></i>
                                                                    {locationLabel || districtName}
                                                                </div>
                                                            )}
                                                            {canOpen && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-primary w-100"
                                                                    onClick={() => openDetails(item)}
                                                                >
                                                                    عرض التفاصيل
                                                                </button>
                                                            )}
                                                        </div>
                                                    </Popup>
                                                </Marker>
                                            );
                                        })}
                                </MapContainer>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
